import { Deal } from '../../../models/deals'
import { useForm, FieldPath } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextInput } from '../../FormUtils/TextInput'
import React, { useEffect, useState } from 'react'
import Select from '../../FormUtils/Select'
import apiService from '../../../services/api'
import { Modal } from 'components/UI/Modal/Modal'
import { toast } from 'react-toastify'
import { featureFlagService } from 'utils/featureFlagService'
import { ChevronDown, ChevronUp } from 'lucide-react'
import {
  checkDoorSelectionRules,
  selectDoorsStepFormSchema,
  SelectDoorsStepWithControl,
} from '../CreateDealModal/SelectDoorsStep'
import { SingleVariantPicker } from 'components/UI/SingleVariantPicker'
import type { SelectedVariant } from 'components/UI/ProductSearchModal'
import { useQueryClient } from '@tanstack/react-query'

interface EditDealModalProps {
  show: boolean
  onClose: () => void
  deal?: Deal
  dealsTableQueryKey: string[]
}

const editDealSchema = z
  .object({
    name: z
      .union([
        z.string().max(100, 'Deal name must be 100 characters or less'),
        z.null(),
        z.undefined(),
      ])
      .optional()
      .nullable(),
    monthly_volume_override: z.union([
      z.number().min(0).optional().nullable(),
      z.literal(''),
    ]),
    monthly_revenue_override: z.union([
      z.number().min(0).optional().nullable(),
      z.literal(''),
    ]),
    close_date_override: z.string().optional().nullable(),
    account_owner: z.number().optional().nullable(),
    sales_stage: z.number().optional().nullable(),
    product_variant_id: z.number().optional().nullable(),
    notes: z.string().optional().nullable(),
    attachments: z.array(z.any()).optional().nullable(),
    origin_campaign_id: z.number().optional().nullable(),
    product_id: z.number(),
  })
  .merge(selectDoorsStepFormSchema)
  .superRefine((v, ctx) => {
    checkDoorSelectionRules(v, ctx)
  })
type EditDealFormValues = z.infer<typeof editDealSchema>

export function EditDealModal(props: EditDealModalProps) {
  const queryClient = useQueryClient()
  const featureFlag = featureFlagService()
  const api = apiService()
  const [overridesExpanded, setOverridesExpanded] = useState(false)

  const { data: stageOptions } = api.useGetSalesStageOptions()
  const { data: userOptions } = api.useGetUserOptions({ onlyActive: true })
  const { data: productOptions } = api.useGetProductsOptions()

  const { data: campaignOptions } = api.useGetCampaignsOptions()
  const [loading, setLoading] = React.useState(false)
  const [initialDoorIds, setInitialDoorIds] = React.useState<number[]>([])
  const [selectedVariant, setSelectedVariant] =
    useState<SelectedVariant | null>(null)
  const { data: products } = api.useGetProducts()

  const handleClose = () => {
    setInitialDoorIds([])
    props.onClose()
  }
  // SelectDoorStepWithControl requires the form to be typed
  const form = useForm<EditDealFormValues>({
    resolver: zodResolver(editDealSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
  })

  const {
    formState: { isValid },
    setValue,
  } = form

  useEffect(() => {
    if (!props.deal) return
    form.reset({
      name: props.deal?.name || '',
      product_id: props.deal.product_id,
      product_variant_id: props.deal?.product_variant_id,
      monthly_volume_override: props.deal?.monthly_volume_override,
      monthly_revenue_override: props.deal?.monthly_revenue_override,
      close_date_override: props.deal?.close_date_override,
      account_owner: props.deal?.account_owner,
      sales_stage: props.deal?.sales_stage,
      notes: '', // Notes always reset to empty
      attachments: [], // Attachments always reset to empty
      origin_campaign_id: props.deal?.origin_campaign,
      apply_to: props.deal?.apply_to,
    })
  }, [props.deal, props.show])

  const onSubmit = form.handleSubmit(async (values) => {
    if (!props.deal) return

    const body = {
      name: values.name || undefined,
      product_id: values.product_id,
      product_variant_id: values.product_variant_id ?? undefined,
      monthly_volume_override: values.monthly_volume_override || null,
      monthly_revenue_override: values.monthly_revenue_override || null,
      close_date_override: values.close_date_override ?? undefined,
      account_owner: values.account_owner ?? undefined,
      sales_stage: values.sales_stage ?? undefined,
      notes: values.notes ?? undefined,
      attachments: values.attachments ?? undefined,
      origin_campaign_id: values.origin_campaign_id
        ? values.origin_campaign_id.toString()
        : undefined,
      apply_to: values.apply_to,
      door_ids: values.door_ids,
    }

    setLoading(true)
    await api.editDeal(props.deal.id, body)
    toast.success('Deal updated successfully')
    setLoading(false)

    handleClose()

    await new Promise((resolve) => setTimeout(resolve, 1000))
    await queryClient.refetchQueries({ queryKey: props.dealsTableQueryKey })
  })

  useEffect(() => {
    // Only load initial door ids if apply_to === 'select_locations'
    if (props.deal && props.deal.apply_to === 'select_locations') {
      api.getSelectedDoorIdsForDeal(props.deal.id).then((result) => {
        setInitialDoorIds(result.ids ?? [])
        form.reset({
          door_ids: result.ids ?? [],
        })
      })
    }
  }, [props.deal, form.reset, setInitialDoorIds])

  // Initialize selected variant from existing form values
  useEffect(() => {
    if (!props.deal || !products) return

    const variantId = props.deal.product_variant_id
    if (variantId) {
      // Find the product for this deal
      const productId = props.deal.product_id
      const product = products.find((p) => p.id === productId)

      if (product?.variants) {
        // Find the variant details
        const variant = product.variants.find((v) => v.id === variantId)
        if (variant) {
          setSelectedVariant({
            id: variant.id!,
            name: variant.name,
            format_pack_size: variant.format_pack_size || null,
            sku: variant.sku || '',
            gtin: variant.gtin || null,
            product_id: product.id,
            product_name: product.name,
            distributor_codes: variant.distributor_codes || [],
          })
        }
      }
    }
  }, [props.deal, products])

  return (
    <Modal
      loading={loading}
      onAccept={onSubmit}
      onCancel={handleClose}
      title="Edit Deal"
      open={props.show}
      onOpenChange={(open) => !open && props.onClose()}
      acceptButtonText="Save"
      size="lg"
      acceptButtonProps={{
        disabled: !isValid,
      }}
    >
      <div className="space-y-6">
        {/* Deal Name Field - Full Width */}
        <div>
          <TextInput
            control={form.control}
            name="name"
            label="Deal Name"
            placeholder={props.deal?.contact_company?.name || 'Company Name'}
            optional={true}
            maxLength={100}
          />
        </div>

        {/* Product Selection */}
        <div>
          <Select
            label={'Product'}
            control={form.control}
            name={`product_id`}
            options={productOptions}
            placeholder="Select..."
            className="mt-1"
            isClearable={false}
          />
        </div>

        {/* Product Variant Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Product Variant
            <span className="text-gray-500 ml-1">(Optional)</span>
          </label>
          <SingleVariantPicker
            productId={form.watch('product_id') || null}
            selectedVariant={selectedVariant}
            onVariantSelect={(variant) => {
              const fieldName =
                'product_variant_id' as FieldPath<EditDealFormValues>

              if (variant) {
                // Set the variant in form and local state
                setValue(fieldName, variant.id, {
                  shouldValidate: true,
                })
                setSelectedVariant(variant)
              } else {
                // Clear the variant
                setValue(fieldName, null, { shouldValidate: true })
                setSelectedVariant(null)
              }
            }}
            buttonText="Select Variant"
            disabled={!form.watch('product_id')}
          />
        </div>

        {/* Sales Stage, Account Owner, and Origin Campaign Row */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Select
              label={'Sales Stage'}
              control={form.control}
              name={`sales_stage`}
              options={stageOptions}
              placeholder="Target"
              className="mt-1"
            />
          </div>
          <div>
            <Select
              label={'Account Owner'}
              control={form.control}
              name={`account_owner`}
              options={userOptions}
              placeholder="Select..."
              className="mt-1"
            />
          </div>
          <div>
            {featureFlag.enableCampaigns ? (
              <Select
                label={'Origin Campaign'}
                control={form.control}
                name={`origin_campaign_id`}
                options={campaignOptions}
                placeholder="Select..."
                className="mt-1"
              />
            ) : (
              <div className="h-0" />
            )}
          </div>
        </div>

        {/* Deal Overrides Section - Collapsible */}
        <div className="bg-[#f4f7f5] rounded-lg p-4 mb-4">
          <button
            type="button"
            onClick={() => setOverridesExpanded(!overridesExpanded)}
            className="w-full flex items-center justify-between text-left"
          >
            <div className="flex items-center gap-2">
              <h4 className="text-sm font-medium text-gray-900">
                Deal Overrides
              </h4>
              <span className="bg-[#d5dcd9] text-[#6b7280] text-xs px-2 py-1 rounded-full">
                Optional
              </span>
            </div>
            {overridesExpanded ? (
              <ChevronUp className="h-5 w-5 text-gray-500" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-500" />
            )}
          </button>

          {overridesExpanded && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <TextInput
                  control={form.control}
                  label={'Est. Monthly Volume'}
                  name={`monthly_volume_override`}
                  type="number"
                  placeholder="2000"
                  optional={true}
                />
                <TextInput
                  control={form.control}
                  label={'Est. Monthly Revenue'}
                  name={`monthly_revenue_override`}
                  type="number"
                  placeholder="$30,000"
                  optional={true}
                />
                <TextInput
                  control={form.control}
                  name={`close_date_override`}
                  label={'Est. Close Date'}
                  type="date"
                  optional={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {props.deal && (
        <SelectDoorsStepWithControl
          control={form.control}
          chainProxyId={props.deal!.contact_company.id}
          initialDoorIds={initialDoorIds}
          formTrigger={form.trigger}
        />
      )}
    </Modal>
  )
}
